import { IClient, Permission } from "@edgetier/watchtower-types";
import { WatchtowerVersion } from "@edgetier/watchtower-types";
import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { ReactNode } from "react";
import { IndexRouteObject, LazyRouteFunction, NonIndexRouteObject, Params, RouteObject } from "react-router-dom";
import { FEATURE_FLAGS } from "types-for/feature-flags";

type IRouteHandleSimplePermission = Permission | Permission[];
type IRouteHandleComplexPermission = {
    type: "tags";
};
type IEnabledClientKeyConfiguration = {
    clientKey: keyof IClient;
    validation: (value?: any) => any;
};
type IEnabledFeatureFlagConfiguration = {
    validation: { featureFlag: FEATURE_FLAGS | Array<FEATURE_FLAGS> };
};
type IEnabledBasicConfiguration = {
    validation: () => boolean;
};
export type IEnabledConfiguration =
    | IEnabledClientKeyConfiguration
    | IEnabledFeatureFlagConfiguration
    | IEnabledBasicConfiguration;

export const isEnabledFeatureFlagConfiguration = (
    enabledConfiguration: IEnabledConfiguration
): enabledConfiguration is IEnabledFeatureFlagConfiguration => {
    return "validation" in enabledConfiguration && "featureFlag" in enabledConfiguration.validation;
};

export type IRouteHandleCrumbItem = { url?: string; title: ReactNode };
export type IRouteHandleBreadcrumbFunctionReturn = IRouteHandleCrumbItem | Array<IRouteHandleCrumbItem>;
type IRouteHandleBreadcrumbFunction = (params: Params) => IRouteHandleBreadcrumbFunctionReturn;

export type IRouteHandlePermissionValue = IRouteHandleSimplePermission | IRouteHandleComplexPermission;
export type IRouteHandleEnabledValue = IEnabledConfiguration | Array<IEnabledConfiguration>;
export type IRouteHandleBreadcrumbValue = IRouteHandleSimplePermission | IRouteHandleComplexPermission;

export type IRouteHandlePermissions = {
    permission: IRouteHandlePermissionValue;
    operation: "AND" | "OR";
};
export type IRouteHandleEnabled = {
    enabled: IRouteHandleEnabledValue;
};
export type IRouteHandleBreadcrumb = {
    crumb: IRouteHandleBreadcrumbFunction;
};
export type IRouteHandle = Partial<IRouteHandlePermissions> &
    Partial<IRouteHandleBreadcrumb> &
    Partial<IRouteHandleEnabled>;

export type IRenderByVersion<Type> = Record<WatchtowerVersion, Type>;

export interface IIndexRouteConfig extends Omit<IndexRouteObject, "lazy"> {
    path?: RouteObject["path"];
    index: true;
    lazy?: LazyRouteFunction<any> | Record<WatchtowerVersion, LazyRouteFunction<any>>;
    loader?: RouteObject["loader"];
    handle?: IRouteHandle;
    initialProperties?: Record<string, any>;
    isRoute?: boolean;
    children?: undefined;
    permission?: Permission;
}

export interface INonIndexRouteConfig extends Omit<NonIndexRouteObject, "children" | "lazy"> {
    index?: false;
    path?: RouteObject["path"];
    lazy?: LazyRouteFunction<any> | Record<WatchtowerVersion, LazyRouteFunction<any>>;
    loader?: RouteObject["loader"];
    handle?: IRouteHandle;
    initialProperties?: Record<string, any>;
    isRoute?: boolean;
    children?: IRouteHierarchy;
    permission?: Permission;
}

export type IRouteConfig = IIndexRouteConfig | INonIndexRouteConfig;

export type IRouteHierarchy = {
    [routeName in PageNames]?: IRouteConfig;
} & { [keys: string]: IRouteConfig };

export interface IRouteConfiguration {
    importPaths: string;
    initialProps?: Record<string, any>;
    children?: IRouteConfiguration[];
}

export const isIRouteHandleSimplePermission = (
    permissionConfiguration: IRouteHandlePermissionValue
): permissionConfiguration is IRouteHandleSimplePermission => {
    return typeof permissionConfiguration === "number" || Array.isArray(permissionConfiguration);
};

export enum PageNames {
    Login = "Login",
    Authenticate = "Authenticate",
    AuthenticateRefresh = "AuthenticateRefresh",
    NoMatch = "NoMatch",
    Main = "Main",
    Home = "Home",
    Agent = "Agent",
    Agents = "Agents",
    Reviews = "Reviews",
    AgentGroup = "AgentGroup",
    Anomalies = "Anomalies",
    Anomaly = "Anomaly",
    CustomerOverview = "CustomerOverview",
    CustomReports = "CustomReports",
    CustomReportsDashboard = "CustomReportsDashboard",
    Explore = "Explore",
    ExploreTags = "ExploreTags",
    FrequentCustomers = "FrequentCustomers",
    Interaction = "Interaction",
    ReviewInteraction = "ReviewInteraction",
    ReviewAgent = "ReviewAgent",
    Live = "Live",
    ManageTags = "ManageTags",
    ManageTagsNew = "ManageTagsNew",
    NewManageTagsEmptyState = "NewManageTagsEmptyState",
    ManageTagsEmptyState = "ManageTagsEmptyState",
    NewViewPhraseTriggers = "NewViewPhraseTriggers",
    ViewPhraseTriggers = "ViewPhraseTriggers",
    NewCreatePhraseTriggers = "NewCreatePhraseTriggers",
    CreatePhraseTriggers = "CreatePhraseTriggers",
    NewManagePhraseTriggers = "NewManagePhraseTriggers",
    ManagePhraseTriggers = "ManagePhraseTriggers",
    NewPhraseTriggersReport = "NewPhraseTriggersReport",
    PhraseTriggersReport = "PhraseTriggersReport",
    Permission = "Permission",
    Scorecards = "Scorecards",
    CreateScorecard = "CreateScorecard",
    EditScorecard = "EditScorecard",
    SpellingConfiguration = "SpellingConfiguration",
    CreateTeamConfiguration = "CreateTeamConfiguration",
    EditTeamConfiguration = "EditTeamConfiguration",
    Teams = "Teams",
    TeamsConfiguration = "TeamsConfiguration",
    Users = "Users",
    ContactReasons = "ContactReasons",
    ContactReasonsEmptyState = "ContactReasonsEmptyState",
    ContactReasonView = "ContactReasonView",
    AiAssistant = "AiAssistant",
    AiAssistantEmptyState = "AiAssistantEmptyState",
    AiAssistantConversation = "AiAssistantConversation",
}

export enum NavigationCategories {
    Home = "Home",
    AgentReview = "Agent Review",
    Anomalies = "Anomalies",
    Explore = "Explore",
    Customers = "Customers",
    CustomerOverview = "CustomerOverview",
    Tags = "Tags",
    Configuration = "Settings",
    Activity = "Activity",
    Reviews = "Reviews",
}

export enum OldNavigationCategories {
    Home = "Home",
    AgentReview = "Agent Review",
    Anomalies = "Anomalies",
    Explore = "Explore",
    Customers = "Customers",
    CustomerOverview = "CustomerOverview",
    Configuration = "Configuration",
    Tags = "Tags",
    UserManagement = "User Management",
    Activity = "Activity",
    Reviews = "Reviews",
}

export enum OldNavigationLabels {
    Home = "Home",
    Agent = "Agent",
    Agents = "Agents",
    Reviews = "Reviews",
    Activity = "Activity",
    Anomalies = "Anomalies",
    Explore = "Explore",
    ExploreTags = "Explore Tags",
    CustomerOverview = "Customers",
    CustomerOverviewSubmenuItem = "Customer Overview",
    Customers = "Customers",
    FrequentCustomers = "Frequent Customers",
    Live = "Live",
    CustomReports = "Custom Reports",
    TagsAndTriggers = "Tags and Triggers",
    AgentGroups = "Agent Groups",
    Scorecards = "Scorecards",
    SpellingExemptions = "Spelling Exemptions",
    Permissions = "Roles and Permissions",
    Users = "Users",
    TeamsConfiguration = "Data Access Control",
    Configuration = "Configuration",
    PhraseTags = "Phrase Tags",
}

export enum NavigationLabels {
    Home = "Home",
    Agent = "Agent Performance",
    Agents = "Agents",
    AgentReview = "Coach",
    Activity = "Activity",
    Reviews = "Reviews",
    Anomalies = "Sonar",
    Explore = "Explore",
    ExploreTags = "Explore Tags",
    CustomerOverview = "Customers",
    CustomerOverviewSubmenuItem = "Customer Overview",
    Customers = "Customers",
    FrequentCustomers = "Frequent Customers",
    Live = "Live",
    CustomReports = "Custom Reports",
    TagsAndTriggers = "Tags & Triggers",
    PhraseTags = "Phrase Tags",
    AgentGroups = "Agent Groups",
    Scorecards = "Scorecards",
    SpellingExemptions = "Spelling Exemptions",
    Permissions = "Roles & Permissions",
    Users = "Users",
    TeamsConfiguration = "Teams & Data",
    Configuration = "Configuration",
    ContactReasons = "Contact Reasons",
    AiAssistant = "Ask",
}

export enum BlockedNavigationStates {
    Blocked = "blocked",
    Unblocked = "unblocked",
    Proceeding = "proceeding",
}

export type AbsolutePageFunction = (...args: Array<string>) => string;

export type IMainRoutes =
    | PageNames.Home
    | PageNames.Explore
    | PageNames.Agent
    | PageNames.Live
    | PageNames.Agents
    | PageNames.Reviews
    | PageNames.ExploreTags
    | PageNames.FrequentCustomers
    | PageNames.CustomerOverview;

export interface INavigationHierarchyPage {
    icon: IconDefinition;
    label: NavigationLabels | NavigationCategories | OldNavigationLabels;
    permission: Permission | Permission[];
}
export interface INavigationHierarchyChildren extends INavigationHierarchyPage {
    pageName: PageNames;
    page: string;
}
export interface INavigationHierarchyItem extends INavigationHierarchyPage {
    pageName?: PageNames;
    page?: string;
    defaultPage?: string;
    children?: INavigationHierarchyChildren[];
}
export type INavigationHierarchy = {
    [index: string]: INavigationHierarchyItem;
};
